import React, { useEffect, useMemo, useState } from 'react';
import {
  buildDashboardMonthCells,
  DASHBOARD_CAL_DOW_EN,
  formatDashboardMonthDigit,
  type DashboardMonthRef,
} from '../../utils/dashboardYearMonths';
import { dashboardMonthCalendarArtIndex } from '../../utils/dashboardCalendarArt';
import {
  getCachedDashboardCalendarArtUrl,
  peekCachedDashboardCalendarArtUrl,
} from '../../utils/dashboardCalendarArtCache';
import {
  buildMonthOutOfTermFlags,
  type AcademicTermDateRange,
} from '../../utils/dashboardAcademicHolidays';

type Props = {
  academicYear: number;
  monthRef: DashboardMonthRef;
  /** 휴일·재량휴업일 빨강 표시 (학사일정 캐시 ISO) */
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso?: Readonly<Record<string, string>>;
  /** 1·2학기 기간 — 이 밖 날짜는 흐림 */
  termRanges?: ReadonlyArray<AcademicTermDateRange>;
  todayIso?: string;
  /** 현재 대시보드가 보고 있는 날짜(선택 강조) */
  selectedIso?: string;
  onHolidayHover?: (el: HTMLElement | null, name: string) => void;
  /** 날짜 클릭 — 데일리/위클리 이동 */
  onSelectDate?: (iso: string) => void;
};

function holidayNameFor(
  iso: string | null | undefined,
  holidayIsoSet: ReadonlySet<string>,
  holidayNoteByIso: Readonly<Record<string, string>> | undefined
): string {
  if (!iso || !holidayIsoSet.has(iso)) return '';
  return (holidayNoteByIso?.[iso] ?? '').trim() || '공휴일';
}

const DashboardMonthCalendarCard: React.FC<Props> = ({
  academicYear,
  monthRef,
  holidayIsoSet,
  holidayNoteByIso,
  termRanges,
  todayIso,
  selectedIso,
  onHolidayHover,
  onSelectDate,
}) => {
  const cells = useMemo(
    () => buildDashboardMonthCells(monthRef.year, monthRef.month),
    [monthRef.year, monthRef.month]
  );
  const outOfTermFlags = useMemo(
    () => buildMonthOutOfTermFlags(cells, termRanges),
    [cells, termRanges]
  );
  const digit = formatDashboardMonthDigit(monthRef.month);
  const artIndex = useMemo(
    () => dashboardMonthCalendarArtIndex(academicYear, monthRef.year, monthRef.month),
    [academicYear, monthRef.year, monthRef.month]
  );
  const [artSrc, setArtSrc] = useState<string | null>(() =>
    artIndex != null ? peekCachedDashboardCalendarArtUrl(artIndex) : null
  );
  const [artFailed, setArtFailed] = useState(false);

  useEffect(() => {
    if (artIndex == null) {
      setArtSrc(null);
      setArtFailed(false);
      return;
    }
    let cancelled = false;
    setArtFailed(false);
    setArtSrc(peekCachedDashboardCalendarArtUrl(artIndex));
    void getCachedDashboardCalendarArtUrl(artIndex).then((url) => {
      if (cancelled) return;
      if (url) setArtSrc(url);
      else setArtFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [artIndex]);

  return (
    <article
      className="cp-dash-month-cal"
      aria-label={`${monthRef.year}년 ${monthRef.month}월`}
    >
      <div className="cp-dash-month-cal-art" aria-hidden>
        {artSrc && !artFailed ? (
          <img
            key={artSrc}
            className="cp-dash-month-cal-art-img"
            src={artSrc}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setArtFailed(true)}
          />
        ) : (
          <span className="cp-dash-month-cal-art-month">{digit}</span>
        )}
      </div>

      <div className="cp-dash-month-cal-main">
        <div className="cp-dash-month-cal-dows" role="row">
          {DASHBOARD_CAL_DOW_EN.map((label) => (
            <span
              key={label}
              className={
                'cp-dash-month-cal-dow' +
                (label === 'SUN' ? ' is-sun' : label === 'SAT' ? ' is-sat' : '')
              }
              role="columnheader"
            >
              {label}
            </span>
          ))}
        </div>
        <div className="cp-dash-month-cal-rule" aria-hidden />
        <div className="cp-dash-month-cal-days" role="grid">
          {cells.map((c, idx) => {
            const isOutOfTerm = !!outOfTermFlags[idx];
            if (c.day == null) {
              return (
                <span
                  key={`e-${idx}`}
                  className={
                    'cp-dash-month-cal-cell is-empty' + (isOutOfTerm ? ' is-out-of-term' : '')
                  }
                  aria-hidden
                />
              );
            }
            const isSun = c.dow === 0;
            const isSat = c.dow === 6;
            const isHoliday = !!c.iso && holidayIsoSet.has(c.iso);
            const isToday = !!c.iso && !!todayIso && c.iso === todayIso;
            const isSelected = !!c.iso && !!selectedIso && c.iso === selectedIso;
            const holidayName = holidayNameFor(c.iso, holidayIsoSet, holidayNoteByIso);
            const tone = isSun || isHoliday ? 'is-sun' : isSat ? 'is-sat' : '';
            const cellClass = `cp-dash-month-cal-cell ${tone}${
              isHoliday ? ' is-holiday' : ''
            }${isToday ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}${
              isOutOfTerm ? ' is-out-of-term' : ''
            }${onSelectDate ? ' is-selectable' : ''}`.trim();
            const ariaLabel = holidayName
              ? `${monthRef.month}월 ${c.day}일 ${holidayName}`
              : isToday
                ? `${monthRef.month}월 ${c.day}일 오늘`
                : `${monthRef.month}월 ${c.day}일`;
            const hoverHandlers = holidayName
              ? {
                  onMouseEnter: (e: React.MouseEvent<HTMLElement>) =>
                    onHolidayHover?.(e.currentTarget, holidayName),
                  onMouseLeave: () => onHolidayHover?.(null, ''),
                }
              : {};
            if (onSelectDate && c.iso) {
              const iso = c.iso;
              return (
                <button
                  key={iso}
                  type="button"
                  className={cellClass}
                  aria-label={ariaLabel}
                  aria-current={isSelected ? 'date' : undefined}
                  onClick={() => onSelectDate(iso)}
                  {...hoverHandlers}
                >
                  {String(c.day).padStart(2, '0')}
                </button>
              );
            }
            return (
              <span
                key={c.iso ?? idx}
                className={cellClass}
                role="gridcell"
                aria-label={ariaLabel}
                {...hoverHandlers}
              >
                {String(c.day).padStart(2, '0')}
              </span>
            );
          })}
        </div>
      </div>
    </article>
  );
};

export default DashboardMonthCalendarCard;
